/**
 * Roles d'une manche (module pur, teste).
 *
 * Repond a deux questions, et a elles seules : qui dessine, et qui cherche la
 * retouche de qui. Tout le reste (scores, eliminations, phases) est ailleurs.
 *
 * Les indices manipules ici sont ceux de la liste des joueurs ENCORE EN JEU,
 * pas de la liste complete du salon. C'est a l'appelant de faire la traduction :
 * ainsi la rotation ne se casse pas quand un joueur est elimine ou se
 * deconnecte, et ce module n'a pas a connaitre la notion de joueur elimine.
 */
import { guessTargetIndex } from './protocol.js';
import { DEFAULT_GAME_MODE } from './constants.js';
/**
 * Repartition des roles pour une manche.
 *
 * `count` est le nombre de joueurs encore en jeu, `round` commence a 1.
 */
export function roundRoles(mode, round, count, 
/**
 * Indice du streamer dans la liste des joueurs en jeu, en mode `stream`
 * uniquement. null partout ailleurs : les autres modes ne connaissent pas
 * cette notion, et ce module n'a pas a savoir qui est l'hote.
 */
streamerIndex = null) {
    if (count <= 0)
        return { drawers: [], targets: [] };
    if (count === 1)
        return { drawers: [0], targets: [null] };
    if (hostIsStreamer(mode) && streamerIndex !== null) {
        // Le streamer ne dessine pas : il n'a pas de retouche a cacher, donc rien
        // que son public puisse chercher. Il est aussi le SEUL a chercher, mais
        // cela ne se lit pas dans `targets` : la chasse commune ne designe aucune
        // cible unique, chacun cherche tout ce qui n'est pas a lui. `targets` reste
        // donc vide, comme pour les autres modes a tableau commun.
        return {
            drawers: Array.from({ length: count }, (_, index) => index).filter((index) => index !== streamerIndex),
            targets: Array.from({ length: count }, () => null),
        };
    }
    if (mode === 'oneDraws') {
        // Un seul dessine, et le tour passe a chaque manche pour que chacun y
        // passe autant de fois. Tous les autres cherchent SA retouche ; lui ne
        // cherche rien, il attend d'etre trouve.
        const drawer = ((round - 1) % count + count) % count;
        return {
            drawers: [drawer],
            targets: Array.from({ length: count }, (_, index) => (index === drawer ? null : drawer)),
        };
    }
    // classic, hunt, battle : tout le monde dessine. Dans les modes a
    // chasse commune, `targets` ne sert pas (chacun cherche TOUT le monde), mais
    // il reste rempli pour que la notion « je ne cherche jamais ma propre
    // retouche » soit vraie dans tous les modes.
    return {
        drawers: Array.from({ length: count }, (_, index) => index),
        targets: Array.from({ length: count }, (_, index) => guessTargetIndex(index, round, count)),
    };
}
/**
 * Le mode joue-t-il sur UN SEUL tableau, une zone par joueur, avec une chasse
 * simultanee ou chacun cherche les retouches de tous les autres ?
 *
 * C'est la vraie ligne de partage cote serveur : tirage d'un tableau commun,
 * message `hunt`, clics multiples. L'elimination, elle, est une couche par
 * dessus que seul `battle` active.
 */
export function isSharedHunt(mode) {
    return mode === 'hunt' || mode === 'battle' || mode === 'stream' || mode === 'contest';
}
/**
 * Le mode se joue-t-il au VOTE plutot qu'a la recherche ?
 *
 * `contest` partage tout le transport de la chasse commune (un tableau pour
 * tous, un message `hunt`, une trame binaire par retouche) et n'en garde rien
 * d'autre : la zone est la MEME pour tout le monde, il n'y a rien a trouver, et
 * la phase de recherche devient une phase de vote qui va toujours au bout de son
 * chrono, puisqu'on peut changer d'avis jusqu'a la derniere seconde.
 */
export function isContest(mode) {
    return mode === 'contest';
}
/**
 * L'hote est-il le STREAMER : celui qui ne dessine pas, cherche seul et ne
 * marque aucun point ?
 *
 * Un seul mode l'active. C'est le predicat que consultent le serveur (pour les
 * roles, la cloture de phase et le score) et le client (pour la pastille verte
 * a cote du pseudo) : le mode ne doit jamais etre teste en dur ailleurs.
 */
export function hostIsStreamer(mode) {
    return mode === 'stream';
}
/**
 * Ce mode deviendra-t-il payant A LA CREATION d'une partie ?
 *
 * Deux exceptions, pour deux raisons differentes :
 *  - le mode par DEFAUT, parce qu'une premiere partie ne se paie pas ;
 *  - `stream`, qui reste gratuit pour toujours : une partie diffusee en direct
 *    montre le jeu a un public entier, elle rapporte plus qu'elle ne coute.
 *
 * Rejoindre restera toujours gratuit, quel que soit le mode : seul l'hote paie.
 */
export function isPremiumMode(mode) {
    // Trois modes gratuits, et ils le resteront : le mode par defaut (une
    // premiere partie ne se paie pas), `stream` (une partie diffusee en direct
    // montre le jeu a un public entier, la faire payer taxerait notre propre
    // vitrine) et `collab`, ou l'on ne joue contre personne.
    return mode !== DEFAULT_GAME_MODE && mode !== 'stream' && mode !== 'collab';
}
/** Le mode elimine-t-il un joueur a chaque manche ? */
export function isElimination(mode) {
    return mode === 'battle';
}
/**
 * Classement d'une manche de battle royale : L'ORDRE DE DECOUVERTE, a l'envers.
 *
 * Le premier trouve finit dernier, et il est le seul elimine. Les autres
 * continuent pourtant de chercher jusqu'au bout de la manche : cela ne change
 * plus rien a la mecanique, mais chacun repart avec un rang, ce qui vaut mieux
 * qu'un « vous avez survecu » indistinct.
 *
 * Ceux qu'on n'a JAMAIS trouves sont premiers EX AEQUO. C'est du classement
 * sportif ordinaire : deux premiers, puis un troisieme, la place 2 restant
 * vide. Personne trouve = tout le monde premier, et personne n'est elimine.
 *
 * REMPLACE le classement au score : ce mode ne compte plus de points du tout.
 * Le score recompensait l'ensemble de la partie, mais il demandait au joueur de
 * suivre deux monnaies a la fois (des points ET une survie) pour un mode dont
 * la seule question est « qui reste ». Attention en revanche a ne pas revenir a
 * « tous les trouves sont elimines » : cette regle-la sortait plusieurs joueurs
 * par manche, parfois tout le monde d'un coup, et la partie s'arretait en deux
 * manches. Un seul sort, le premier trouve.
 *
 * @param foundOrder identifiants des auteurs, dans l'ordre ou leurs retouches
 *                   ont ete decouvertes.
 * @param inPlay     identifiants de tous les joueurs de la manche.
 */
export function huntPlacements(foundOrder, inPlay) {
    const places = new Map();
    const total = inPlay.length;
    let position = 0;
    for (const id of foundOrder) {
        if (!inPlay.includes(id) || places.has(id))
            continue;
        places.set(id, total - position);
        position += 1;
    }
    // Jamais trouves : premiers, tous ensemble.
    for (const id of inPlay) {
        if (!places.has(id))
            places.set(id, 1);
    }
    return places;
}
/**
 * Le mode a-t-il un nombre de manches fixe ?
 *
 * En battle royale, non : la partie dure ce qu'il faut pour qu'il ne reste
 * qu'un joueur. Afficher « manche 2/5 » y serait un mensonge.
 */
export function hasFixedRounds(mode) {
    return !isElimination(mode);
}
/**
 * Le reglage de manches compte-t-il des TOURS COMPLETS plutot que des manches ?
 *
 * Vrai en mode « un seul dessine » : chacun ne dessine qu'une fois par tour, et
 * la partie n'est equitable que si le tour est complet. Regler « 2 » a trois
 * joueurs donne donc six manches, et tout le monde a dessine deux fois.
 */
export function countsCycles(mode) {
    return mode === 'oneDraws';
}
/** Nombre de manches reellement jouees, pour un reglage et un effectif. */
export function totalRounds(mode, setting, playerCount) {
    return countsCycles(mode) ? setting * Math.max(playerCount, 1) : setting;
}
/**
 * Mode COLLABORATIF : on ne cache rien, on construit ensemble.
 *
 * A N joueurs, N tableaux et N manches. Chacun commence sur un tableau
 * different, puis les tableaux TOURNENT : celui du joueur 1 passe au joueur 2,
 * et ainsi de suite. Chaque joueur dessine donc une fois sur chaque tableau, en
 * completant ce que les precedents ont laisse. Personne ne cherche.
 */
export function isCollab(mode) {
    return mode === 'collab';
}
/**
 * Quel tableau ce joueur retouche a cette manche.
 *
 * Le joueur d'indice `player` commence sur le tableau de meme indice, puis
 * suit la rotation. `round` commence a 1.
 *
 * C'est une rotation et pas un tirage : elle garantit que chacun passe une
 * fois et une seule sur chaque tableau, ce qui est toute la promesse du mode.
 */
export function collabPainting(player, round, count) {
    if (count <= 0)
        return 0;
    // `+ count` : le modulo de JavaScript garde le signe, une manche mal formee
    // donnerait sinon un indice negatif.
    return (((player - (round - 1)) % count) + count) % count;
}
/**
 * Ordre des auteurs d'un tableau, de la premiere retouche a la derniere.
 *
 * C'est l'ordre dans lequel le vote les rejoue, et il n'est PAS celui des
 * joueurs : le tableau `painting` a d'abord ete touche par le joueur de meme
 * indice, puis par le suivant dans la rotation.
 */
export function collabAuthors(painting, count) {
    return Array.from({ length: count }, (_, round) => (painting + round) % count);
}
//# sourceMappingURL=roles.js.map